import Box from "@mui/material/Box";

import Modal from "@mui/material/Modal";
import RegisterUserForm from "./Register";
import { useEffect, useState, useRef } from "react";
import LoginUserForm from "./Login";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Snackbar, Tabs, Tab, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { mergeGuestCart } from "../../../Redux/Customers/Cart/Action";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 500 },
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 2,
};

export default function AuthModal({ handleClose, open }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = useSelector((store) => store);
  // Determine initial tab: 0 = Login, 1 = Register
  const [activeTab, setActiveTab] = useState(location.pathname === "/register" ? 1 : 0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showLockAlert, setShowLockAlert] = useState(false);


  // Track if we've already handled post-auth actions to prevent infinite loops
  const hasHandledPostAuth = useRef(false);
  const previousUserId = useRef(null);

  // Check if user is authenticated

  // Handle post-authentication flow: cart merge and redirect
  // Only runs ONCE per new authentication (when user ID changes from null to a value)
  useEffect(() => {
    // Only proceed if user just logged in (user exists and wasn't there before)
    if (auth.user && auth.user.id !== previousUserId.current) {
      // Mark that we've handled this auth
      previousUserId.current = auth.user.id;

      // Skip if already handled (prevents any race conditions)
      if (hasHandledPostAuth.current) return;
      hasHandledPostAuth.current = true;

      const jwt = localStorage.getItem("jwt");

      // Trigger cart merge ONLY if not already done (use sessionStorage flag)
      const mergeCompleted = sessionStorage.getItem("cartMergeCompleted");
      if (jwt && !mergeCompleted) {
        dispatch(mergeGuestCart(jwt));
        sessionStorage.setItem("cartMergeCompleted", "true");
      }

      // Check if there's a saved checkout state for redirect
      const checkoutStateStr = sessionStorage.getItem("checkoutState");
      if (checkoutStateStr) {
        try {
          const checkoutState = JSON.parse(checkoutStateStr);
          const returnUrl = checkoutState.returnUrl;

          if (returnUrl) {
            // Close modal and navigate to return URL
            handleClose();
            navigate(returnUrl);
            return;
          }
        } catch (error) {
          console.error("Error parsing checkout state:", error);
          sessionStorage.removeItem("checkoutState");
        }
      }

      // Default behavior: just close the modal
      handleClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, dispatch, navigate]); // handleClose intentionally omitted to prevent infinite loops

  // Reset the post-auth flag when modal opens (for next login attempt)
  useEffect(() => {
    if (open) {
      hasHandledPostAuth.current = false;
    }
  }, [open]);

  // Update tab when path changes
  useEffect(() => {
    if (location.pathname === "/register") {
      setActiveTab(1);
    } else if (location.pathname === "/login") {
      setActiveTab(0);
    }
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Allow closing modal - auth is flexible (can happen at any stage)
  const handleModalClose = (event, reason) => {
    // If form has content, prevent accidental closing on backdrop click
    if (isFormDirty && reason === "backdropClick") {
      setShowLockAlert(true);
      return;
    }
    handleClose();
    setIsFormDirty(false); // Reset on close
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        size="large"
      >
        <Box sx={{ ...style, position: 'relative' }}>
          {/* Close button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
              '&:hover': {
                color: 'grey.700',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h5" component="h2" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Welcome to SiriFarms
          </Typography>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{ mb: 3 }}
          >
            <Tab label="Login" />
            <Tab label="Register" />
          </Tabs>

          {activeTab === 0 ? (
            <LoginUserForm onFieldsChange={setIsFormDirty} />
          ) : (
            <RegisterUserForm onFieldsChange={setIsFormDirty} onSwitchToLogin={() => setActiveTab(0)} />
          )}

          <Snackbar
            open={showLockAlert}
            autoHideDuration={3000}
            onClose={() => setShowLockAlert(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="warning" variant="filled">
              Please finish or clear your details before closing.
            </Alert>
          </Snackbar>
        </Box>
      </Modal>

    </>


  );
}

